import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';

// Dashboard data - with improved data extraction and auth checking
export const useDashboardData = () => {
  // Use the consolidated endpoint
  const dashboardQuery = useQuery('dashboardData', () => api.portfolio.getDashboard(), {
    retry: (failureCount, error) => {
      // Auth hatalarında tekrar deneme yapma
      if (error?.response?.status === 401 || 
          error?.response?.status === 403 ||
          error?.authError ||
          error?.handled) {
        console.log('Auth hatası nedeniyle retry yapılmıyor');
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: 1000,
    staleTime: 30000, // 30 seconds
    cacheTime: 300000, // 5 minutes
    // Sadece authenticated kullanıcılar için çalıştır
    enabled: localStorage.getItem('isAuthenticated') === 'true',
    // Window focus olduğunda auth hatası varsa refetch yapma
    refetchOnWindowFocus: (query) => {
      return !query.state.error?.authError;
    },
    onError: (error) => {
      console.error('Dashboard data error:', error);
      
      // Auth hatası kontrolü
      if (error?.response?.status === 401 || 
          error?.response?.status === 403 ||
          error?.response?.data?.message === 'Authentication required' ||
          error?.response?.data?.message === 'Session expired' ||
          error?.authError) {
        
        console.error('Dashboard query auth hatası - auth-error event tetikleniyor');
        
        // Auth error event'i tetikle
        window.dispatchEvent(new CustomEvent('auth-error', {
          detail: {
            source: 'useDashboardData',
            error: error,
            timestamp: Date.now()
          }
        }));
      }
    },
    onSuccess: (data) => {
      console.log('Dashboard data loaded successfully');
    }
  });
  
  // Refetch function
  const refetch = async () => {
    try {
      await dashboardQuery.refetch();
    } catch (error) {
      console.error('Dashboard refetch error:', error);
      throw error;
    }
  };
  
  // Extract data or use defaults
  const extractedData = dashboardQuery.data?.data?.data || {
    summary: {
      totalBalance: 0,
      availableBalance: 0,
      unrealizedPnl: 0,
      marginUsed: 0,
      currency: 'USDT',
      apiConfig: null
    },
    positions: [],
    stats: {
      totalOpen: 0,
      totalLong: 0,
      totalShort: 0,
      totalPnl: 0
    }
  };
  
  // Extract activities from aidata if it exists
  const activities = dashboardQuery.data?.data?.data?.aidata?.activities || [];
  
  return {
    ...extractedData,
    activities,
    isLoading: dashboardQuery.isLoading,
    isError: dashboardQuery.isError,
    error: dashboardQuery.error,
    refetch,
    rawData: dashboardQuery.data
  };
};

// Performance data with auth check
export const usePerformanceData = (filters = {}) => {
  return useQuery(
    ['performanceData', filters], 
    () => api.performance.getData(filters),
    {
      keepPreviousData: true,
      retry: (failureCount, error) => {
        if (error?.response?.status === 401 || error?.authError) {
          return false;
        }
        return failureCount < 1;
      },
      retryDelay: 1000,
      enabled: localStorage.getItem('isAuthenticated') === 'true',
    }
  );
};

// Model details with auth check
export const useModelDetails = (modelId, filters = {}) => {
  return useQuery(
    ['modelDetails', modelId, filters],
    () => api.performance.getModelDetails(modelId, filters),
    {
      enabled: !!modelId && localStorage.getItem('isAuthenticated') === 'true',
      retry: (failureCount, error) => {
        if (error?.response?.status === 401 || error?.authError) {
          return false;
        }
        return failureCount < 1;
      },
      retryDelay: 1000,
    }
  );
};

// Performance symbols with auth check
export const usePerformanceSymbols = () => {
  return useQuery('performanceSymbols', () => api.performance.getSymbols(), {
    retry: (failureCount, error) => {
      if (error?.response?.status === 401 || error?.authError) {
        return false;
      }
      return failureCount < 1;
    },
    retryDelay: 1000,
    enabled: localStorage.getItem('isAuthenticated') === 'true',
  });
};

// Session options with auth check
export const useSessionOptions = () => {
  return useQuery('sessionOptions', () => api.performance.getSessions(), {
    retry: (failureCount, error) => {
      if (error?.response?.status === 401 || error?.authError) {
        return false;
      }
      return failureCount < 1;
    },
    retryDelay: 1000,
    enabled: localStorage.getItem('isAuthenticated') === 'true',
  });
};

// API Settings with auth check
export const useApiSettings = () => {
  return useQuery('apiSettings', () => api.settings.getSettings(), {
    retry: (failureCount, error) => {
      if (error?.response?.status === 401 || error?.authError) {
        return false;
      }
      return failureCount < 1;
    },
    retryDelay: 1000,
    enabled: localStorage.getItem('isAuthenticated') === 'true',
  });
};

// Update API settings mutation
export const useUpdateApiSettings = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    (settings) => api.settings.updateSettings(settings),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('apiSettings');
      },
      onError: (error) => {
        if (error?.response?.status === 401 || error?.authError) {
          window.dispatchEvent(new CustomEvent('auth-error', {
            detail: { source: 'useUpdateApiSettings', error }
          }));
        }
      }
    }
  );
};

// Check connection mutation
export const useCheckConnection = () => {
  return useMutation(() => api.settings.checkConnection(), {
    onError: (error) => {
      if (error?.response?.status === 401 || error?.authError) {
        window.dispatchEvent(new CustomEvent('auth-error', {
          detail: { source: 'useCheckConnection', error }
        }));
      }
    }
  });
};

// Disconnect API mutation
export const useDisconnectApi = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    () => api.settings.disconnect(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('apiSettings');
      },
      onError: (error) => {
        if (error?.response?.status === 401 || error?.authError) {
          window.dispatchEvent(new CustomEvent('auth-error', {
            detail: { source: 'useDisconnectApi', error }
          }));
        }
      }
    }
  );
};

// Trade Logs with auth check
export const useTradeLogs = (page = 1, limit = 25, search = '') => {
  return useQuery(
    ['tradeLogs', page, limit, search],
    () => api.trade.getLogs(page, limit, search),
    {
      keepPreviousData: true,
      retry: (failureCount, error) => {
        if (error?.response?.status === 401 || error?.authError) {
          return false;
        }
        return failureCount < 1;
      },
      retryDelay: 1000,
      staleTime: 30000,
      enabled: localStorage.getItem('isAuthenticated') === 'true',
    }
  );
};

// Trade details with auth check
export const useTradeDetails = (id) => {
  return useQuery(
    ['tradeDetails', id],
    () => api.trade.getTradeDetails(id),
    {
      enabled: !!id && localStorage.getItem('isAuthenticated') === 'true',
      retry: (failureCount, error) => {
        if (error?.response?.status === 401 || error?.authError) {
          return false;
        }
        return failureCount < 1;
      },
      retryDelay: 1000,
      staleTime: 300000,
    }
  );
};

// Authentication - özel olarak ele alınıyor
export const useAuth = () => {
  return useQuery(
    'user', 
    () => api.auth.getUser(), 
    {
      refetchOnWindowFocus: false,
      retry: false, // Auth endpoint'i için retry yapma
      staleTime: 60000, // 1 minute
      onSuccess: (response) => {
        if (response.data?.authenticated) {
          localStorage.setItem('isAuthenticated', 'true');
          if (response.data.user) {
            localStorage.setItem('currentUser', JSON.stringify(response.data.user));
          }
        } else {
          // Açıkça false dönüyorsa temizle
          if (response.data && response.data.authenticated === false) {
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('currentUser');
          }
        }
      },
      onError: (error) => {
        console.error('Auth check error:', error);
        
        // Auth check hatasında localStorage'ı temizle ama redirect yapma
        // Login sayfasındaysa zaten redirect yapmasına gerek yok
        if (error?.response?.status === 401) {
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('currentUser');
        }
      }
    }
  );
};